import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { FileText, AlertCircle } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFCC00] rounded-full mb-6">
            <FileText className="h-8 w-8 text-black" />
          </div>
          <h1 className="text-5xl font-bold font-montserrat mb-4">
            Terms of <span className="text-[#FFCC00]">Service</span>
          </h1>
          <p className="text-gray-400 text-lg">Last Updated: January 23, 2025</p>
        </div>

        <Card className="bg-gray-800/50 border-gray-700 p-8 backdrop-blur-sm">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">1. Agreement to Terms</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              By accessing or using Mic Check Millionaire (&quot;MCM&quot;, &quot;the Platform&quot;, &quot;our service&quot;), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.
            </p>
            <p className="text-gray-300 leading-relaxed">
              These terms apply to all visitors, users, artists, reviewers, and others who access or use the service.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Platform Usage */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">2. Platform Usage Rules</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">For All Users:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>You must be at least 13 years old to use this platform</li>
                  <li>You are responsible for maintaining the confidentiality of your account</li>
                  <li>You agree not to use the platform for any illegal or unauthorized purpose</li>
                  <li>You will not transmit any malware, viruses, or harmful code</li>
                  <li>You will not impersonate any person or entity</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">For Artists:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>You must own or have permission to share all music you upload</li>
                  <li>You retain all rights to your submitted content</li>
                  <li>You grant MCM a license to display and distribute your submissions for review purposes</li>
                  <li>Submission fees (skip/superskip) are non-refundable once processed</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">For Reviewers:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>You must provide honest, constructive feedback</li>
                  <li>You do not own the content you review</li>
                  <li>You are responsible for your own earnings and taxes</li>
                  <li>W-9 or equivalent tax forms may be required for payouts</li>
                  <li>MCM is not responsible for disputes between reviewers and artists</li>
                </ul>
              </div>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Account Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">3. Account Creation & Termination</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              When you create an account with us, you must provide accurate, complete, and current information. Failure to do so constitutes a breach of the Terms.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              You may delete your account at any time by contacting support at hello@miccheckmillionaire.com. We reserve the right to suspend or terminate your account at any time for violations of these Terms or our <Link to="/conduct" className="text-[#FFCC00] hover:underline">Code of Conduct</Link>.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Refund Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">4. Refund Policy</h2>
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  <strong>All submission fees are final and non-refundable.</strong> This includes regular submissions, skip lane, and superskip submissions. Once your track enters the review queue, the fee is considered earned by the reviewer.
                </p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed">
              Exceptions may be made on a case-by-case basis for technical errors or platform issues. Contact support within 24 hours of your submission for refund requests.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Content Ownership */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">5. Content Ownership & Rights</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li><strong>Artists retain full ownership</strong> of all music, lyrics, and content they upload</li>
              <li><strong>Reviewers do not acquire rights</strong> to any content they review</li>
              <li>By uploading content, you grant MCM a non-exclusive, worldwide license to host, display, and distribute your content for platform purposes</li>
              <li>You represent and warrant that you have all necessary rights to upload your content</li>
              <li>MCM respects intellectual property rights and will respond to valid DMCA takedown requests</li>
            </ul>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Reviewer Earnings */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">6. Reviewer Earnings & Payouts</h2>
            <div className="space-y-4 text-gray-300">
              <p className="leading-relaxed">
                Reviewers earn money through tips (Cash App, Twitch subscriptions) and submission fees. All earnings are considered taxable income.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>MCM does not withhold taxes from reviewer earnings</li>
                <li>Reviewers are responsible for reporting income to tax authorities</li>
                <li>W-9 or equivalent tax forms may be required for payouts exceeding $600/year (US)</li>
                <li>MCM acts as a platform facilitator and is not an employer</li>
                <li>Payout timing and methods are subject to payment processor terms</li>
              </ul>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* DMCA Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">7. DMCA & Copyright Policy</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Mic Check Millionaire respects the intellectual property rights of others. If you believe your copyrighted work has been uploaded without permission, please send a DMCA takedown notice to:
            </p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
              <p className="text-white font-semibold mb-2">Copyright Agent:</p>
              <p className="text-gray-300">Email: legal@miccheckmillionaire.com</p>
            </div>
            <p className="text-gray-300 leading-relaxed mt-4">
              Your notice must include: (1) identification of the copyrighted work, (2) identification of the infringing material, (3) your contact information, (4) a statement of good faith belief, and (5) a statement of accuracy under penalty of perjury.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Account Suspension */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">8. Account Suspension & Removal</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We reserve the right to suspend or terminate your account for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li>Violations of these Terms of Service</li>
              <li>Violations of our <Link to="/conduct" className="text-[#FFCC00] hover:underline">Code of Conduct</Link></li>
              <li>Fraudulent activity or abuse of the platform</li>
              <li>Copyright infringement or intellectual property violations</li>
              <li>Any illegal activity or behavior that harms other users</li>
            </ul>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">9. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MIC CHECK MILLIONAIRE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATING TO YOUR USE OF THE PLATFORM.
            </p>
            <p className="text-gray-300 leading-relaxed">
              The platform is provided &quot;as is&quot; without warranties of any kind, express or implied.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Changes to Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">10. Changes to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated &quot;Last Updated&quot; date. Continued use of the platform after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">11. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
              <p className="text-white">Email: <a href="mailto:hello@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline">hello@miccheckmillionaire.com</a></p>
              <p className="text-white mt-2">Support: <Link to="/support" className="text-[#FFCC00] hover:underline">Contact Support Form</Link></p>
            </div>
          </section>
        </Card>

        {/* Footer Links */}
        <div className="mt-12 text-center space-x-6">
          <Link to="/privacy" className="text-[#FFCC00] hover:underline">Privacy Policy</Link>
          <Link to="/conduct" className="text-[#FFCC00] hover:underline">Code of Conduct</Link>
          <Link to="/support" className="text-[#FFCC00] hover:underline">Contact Support</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;